import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  FaWallet,
  FaCoins,
  FaHistory,
  FaArrowUp,
  FaArrowDown,
  FaClock,
  FaExchangeAlt,
  FaGift,
  FaPlusCircle,
  FaMinusCircle,
  FaRupeeSign,
  FaTimes
} from "react-icons/fa";
import CountUp from "react-countup";

const API_BASE_URL = 'https://api.ingrainsystems.com/api';

const MyWallet = () => {
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [rechargeCoins, setRechargeCoins] = useState('');
  const [rechargeLoading, setRechargeLoading] = useState(false);
  const [rechargeMessage, setRechargeMessage] = useState({ text: "", type: "" });

  const getClientId = () => {
    return localStorage.getItem('clientId') || '';
  };

  useEffect(() => {
    fetchWalletData();
    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const clientId = getClientId();
      
      if (!clientId) {
        setError("Client ID not found. Please login again.");
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/clients/my-wallet/${clientId}`);
      
      console.log("Wallet API Response:", response.data);
      
      if (response.data.success) {
        setWalletData(response.data.data);
      } else {
        setError(response.data.message || "Failed to fetch wallet data");
      }
    } catch (err) {
      console.error("Error fetching wallet:", err);
      setError(err.response?.data?.message || "Failed to fetch wallet data");
    } finally {
      setLoading(false);
    }
  };

  const handleRecharge = async () => {
    const coins = parseInt(rechargeCoins);
    
    if (!coins || coins < 1) {
      setRechargeMessage({ text: "Please enter valid number of coins (minimum 1)", type: "error" });
      return;
    }

    try {
      setRechargeLoading(true);
      setRechargeMessage({ text: "", type: "" });
      
      const clientId = getClientId();
      
      // Create Razorpay order
      const orderResponse = await axios.post(`${API_BASE_URL}/clients/recharge-wallet/${clientId}`, {
        coins: coins
      });
      
      if (orderResponse.data.success) {
        const options = {
          key: orderResponse.data.data.razorpayKey,
          amount: orderResponse.data.data.amount,
          currency: orderResponse.data.data.currency,
          name: "Timely Health Care",
          description: `Recharge Wallet with ${coins} Coins`,
          order_id: orderResponse.data.data.orderId,
          handler: async (response) => {
            // Verify payment
            try {
              const verifyResponse = await axios.post(`${API_BASE_URL}/clients/verify-wallet-recharge/${clientId}`, {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              });
              
              if (verifyResponse.data.success) {
                setRechargeMessage({ text: "Wallet recharged successfully!", type: "success" });
                setTimeout(() => {
                  setShowRechargeModal(false);
                  setRechargeCoins('');
                  setRechargeMessage({ text: "", type: "" });
                  fetchWalletData(); // Refresh wallet data
                }, 2000);
              } else {
                setRechargeMessage({ text: verifyResponse.data.message || "Payment verification failed", type: "error" });
              }
            } catch (err) {
              console.error("Verification error:", err);
              setRechargeMessage({ text: err.response?.data?.message || "Payment verification failed", type: "error" });
            }
          },
          prefill: {
            email: localStorage.getItem('clientEmail'),
            name: localStorage.getItem('clientName')
          },
          notes: {
            clientId: clientId,
            coins: coins
          },
          theme: {
            color: "#3B82F6"
          },
          modal: {
            ondismiss: () => {
              setRechargeLoading(false);
              setRechargeMessage({ text: "Payment cancelled", type: "error" });
            }
          }
        };
        
        const razorpayInstance = new window.Razorpay(options);
        razorpayInstance.open();
      } else {
        setRechargeMessage({ text: orderResponse.data.message || "Failed to create order", type: "error" });
      }
    } catch (err) {
      console.error("Recharge error:", err);
      setRechargeMessage({ text: err.response?.data?.message || "Failed to create order", type: "error" });
    } finally {
      setRechargeLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getFilteredTransactions = () => {
    if (!walletData?.walletHistory) return [];
    if (selectedFilter === 'all') return walletData.walletHistory;
    return walletData.walletHistory.filter(t => t.type === selectedFilter);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-sm text-gray-500">Loading wallet...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <div className="bg-white rounded-xl p-6 max-w-md w-full text-center shadow-lg">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <FaWallet className="text-red-500 text-xl" />
          </div>
          <h3 className="text-base font-bold text-gray-800 mb-1">Wallet Error</h3>
          <p className="text-xs text-gray-500 mb-4">{error}</p>
          <button
            onClick={fetchWalletData}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!walletData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <FaWallet className="text-4xl text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No wallet data available</p>
        </div>
      </div>
    );
  }

  const filteredTransactions = getFilteredTransactions();
  const totalCredited = walletData.walletHistory?.filter(t => t.type === 'credit').reduce((sum, t) => sum + t.coins, 0) || 0;
  const totalDebited = walletData.walletHistory?.filter(t => t.type === 'debit').reduce((sum, t) => sum + t.coins, 0) || 0;

  // Get total coins from response - either from totalCoins or wallet.coins
  const totalCoins = walletData.totalCoins || walletData.wallet?.coins || 0;

  return (
    <div className="p-4 md:p-6">
      <div className="max-w-5xl mx-auto">
        
        {/* Balance Card with Recharge Button */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-5 mb-5 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                <FaWallet className="text-white text-xl" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-blue-100 uppercase tracking-wider">Total Balance</p>
                <div className="flex items-baseline gap-1">
                  <FaCoins className="text-yellow-300 text-sm" />
                  <span className="text-2xl font-black text-white">
                    <CountUp end={totalCoins} duration={2} />
                  </span>
                  <span className="text-xs text-blue-200">coins</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowRechargeModal(true)}
                className="bg-white text-blue-600 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-blue-50 transition shadow-md"
              >
                <FaRupeeSign className="inline mr-1 text-[10px]" />
                Recharge
              </button>
            </div>
          </div>
        </div>

        {/* Stats Row - Compact */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-1">
              <FaCoins className="text-emerald-500 text-sm" />
            </div>
            <div className="text-lg font-bold text-gray-800">
              <CountUp end={totalCoins} duration={2} />
            </div>
            <div className="text-[9px] font-bold text-gray-400 uppercase">Total Coins</div>
          </div>
          
          <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-1">
              <FaExchangeAlt className="text-blue-500 text-sm" />
            </div>
            <div className="text-lg font-bold text-gray-800">
              <CountUp end={walletData.totalTransactions || walletData.walletHistory?.length || 0} duration={2} />
            </div>
            <div className="text-[9px] font-bold text-gray-400 uppercase">Transactions</div>
          </div>
          
          <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-1">
              <FaArrowUp className="text-emerald-500 text-sm" />
            </div>
            <div className="text-lg font-bold text-emerald-600">
              +<CountUp end={totalCredited} duration={2} />
            </div>
            <div className="text-[9px] font-bold text-gray-400 uppercase">Total Credited</div>
          </div>
          
          <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-1">
              <FaArrowDown className="text-red-500 text-sm" />
            </div>
            <div className="text-lg font-bold text-red-600">
              -<CountUp end={totalDebited} duration={2} />
            </div>
            <div className="text-[9px] font-bold text-gray-400 uppercase">Total Debited</div>
          </div>
        </div>

        {/* Transaction Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <FaHistory className="text-blue-500 text-sm" />
                <h2 className="text-sm font-bold text-gray-800">Transaction History</h2>
                <span className="text-[10px] text-gray-400">({filteredTransactions.length})</span>
              </div>
              
              <div className="flex gap-1.5">
                <button
                  onClick={() => setSelectedFilter('all')}
                  className={`px-3 py-1 rounded-lg text-[10px] font-bold transition ${
                    selectedFilter === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setSelectedFilter('credit')}
                  className={`px-3 py-1 rounded-lg text-[10px] font-bold transition flex items-center gap-1 ${
                    selectedFilter === 'credit'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <FaArrowUp className="text-[8px]" /> Credits
                </button>
                <button
                  onClick={() => setSelectedFilter('debit')}
                  className={`px-3 py-1 rounded-lg text-[10px] font-bold transition flex items-center gap-1 ${
                    selectedFilter === 'debit'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <FaArrowDown className="text-[8px]" /> Debits
                </button>
              </div>
            </div>
          </div>

          <div className="p-4">
            {filteredTransactions.length > 0 ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {filteredTransactions.map((transaction, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-3 rounded-lg transition ${
                      transaction.type === 'credit' 
                        ? 'bg-emerald-50/50 border border-emerald-100' 
                        : 'bg-red-50/50 border border-red-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        transaction.type === 'credit' 
                          ? 'bg-emerald-500' 
                          : 'bg-red-500'
                      }`}>
                        {transaction.type === 'credit' ? (
                          <FaPlusCircle className="text-white text-[10px]" />
                        ) : (
                          <FaMinusCircle className="text-white text-[10px]" />
                        )}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-800">{transaction.message}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <FaClock className="text-[8px] text-gray-400" />
                          <p className="text-[9px] text-gray-500">{formatDateTime(transaction.createdAt)}</p>
                          <span className="w-0.5 h-0.5 rounded-full bg-gray-300"></span>
                          <p className="text-[9px] text-gray-400">{formatTimeAgo(transaction.createdAt)}</p>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-bold ${
                        transaction.type === 'credit' ? 'text-emerald-600' : 'text-red-600'
                      }`}>
                        {transaction.type === 'credit' ? '+' : '-'} {transaction.coins}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <FaHistory className="text-3xl text-gray-300 mx-auto mb-2" />
                <p className="text-xs text-gray-500">No transactions found</p>
              </div>
            )}

            {/* Summary - Compact */}
            {filteredTransactions.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-2 bg-emerald-50 rounded-lg">
                    <p className="text-[9px] font-bold text-emerald-600 uppercase">Credited</p>
                    <p className="text-sm font-bold text-emerald-700">
                      +{filteredTransactions.filter(t => t.type === 'credit').reduce((sum, t) => sum + t.coins, 0)}
                    </p>
                  </div>
                  <div className="text-center p-2 bg-red-50 rounded-lg">
                    <p className="text-[9px] font-bold text-red-600 uppercase">Debited</p>
                    <p className="text-sm font-bold text-red-700">
                      -{filteredTransactions.filter(t => t.type === 'debit').reduce((sum, t) => sum + t.coins, 0)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info Card - Compact */}
        <div className="mt-4 bg-blue-50 rounded-lg p-3">
          <div className="flex items-center gap-3">
            <FaGift className="text-emerald-500 text-sm" />
            <div className="flex-1">
              <p className="text-[10px] font-bold text-gray-800">About Wallet Coins</p>
              <p className="text-[9px] text-gray-600">1 Coin = ₹1 • Never expires • Use for purchases</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 text-center">
          <p className="text-[8px] text-gray-400 uppercase">
            Last Updated: {formatDateTime(walletData.updatedAt)}
          </p>
        </div>
      </div>

      {/* Recharge Modal */}
      {showRechargeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-base font-bold text-gray-800">Recharge Wallet</h3>
              <button
                onClick={() => {
                  setShowRechargeModal(false);
                  setRechargeCoins('');
                  setRechargeMessage({ text: "", type: "" });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="p-5">
              <div className="mb-4">
                <label className="text-xs font-bold text-gray-700 mb-2 block">Enter Coins (1 Coin = ₹1)</label>
                <div className="relative">
                  <FaCoins className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type="number"
                    value={rechargeCoins}
                    onChange={(e) => setRechargeCoins(e.target.value)}
                    placeholder="Enter number of coins"
                    className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                    min="1"
                  />
                </div>
                <p className="text-[10px] text-gray-500 mt-1">
                  Amount payable: ₹{rechargeCoins || 0}
                </p>
              </div>

              {rechargeMessage.text && (
                <div className={`mb-4 p-2 rounded-lg text-xs ${
                  rechargeMessage.type === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
                }`}>
                  {rechargeMessage.text}
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowRechargeModal(false);
                    setRechargeCoins('');
                    setRechargeMessage({ text: "", type: "" });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecharge}
                  disabled={rechargeLoading || !rechargeCoins}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rechargeLoading ? 'Processing...' : 'Proceed to Pay'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyWallet;